import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { formatCurrency } from '../utils/formatters';

const API = '/api';

const Icon = ({ d, size = 16, color = 'currentColor', strokeWidth = 2, style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" style={style}>
        {Array.isArray(d) ? d.map((path, i) => <path key={i} d={path} />) : <path d={d} />}
    </svg>
);

const ic = {
    plus: 'M12 5v14 M5 12h14',
    x: 'M18 6L6 18 M6 6l12 12',
    edit: 'M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7 M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z',
    trash: 'M3 6h18 M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6 m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2 M10 11v6 M14 11v6',
    search: 'M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z',
    calendar: ['M19 4H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2z', 'M16 2v4', 'M8 2v4', 'M3 10h18'],
    shield: ['M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z'],
    alert: ['M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z', 'M12 9v4', 'M12 17h.01'],
};

export default function Compliance({ user, operatorDetails }) {
    const [records, setRecords] = useState([]);
    const [vehicles, setVehicles] = useState([]);
    const [complianceTypes, setComplianceTypes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [search, setSearch] = useState('');

    const [form, setForm] = useState({
        vehicleId: '',
        type: '',
        status: 'Pending Renewal',
        dueDate: '',
        cost: ''
    });

    useEffect(() => {
        fetchInitialData();
    }, []);

    const fetchInitialData = async () => {
        setLoading(true);
        try {
            const masterRes = await axios.get(`${API}/master-data`);
            const types = masterRes.data.filter(d => d.category === 'compliance-types' && d.active !== false);
            setComplianceTypes(types);

            const opId = user?.operatorId || user?._id || 'all';
            const vehicleRes = await axios.get(`${API}/vehicles/${opId}`);
            setVehicles(vehicleRes.data);

            // Dummy records for display
            setRecords([]);
        } catch (err) {
            console.error('Error fetching compliance data:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleAddRecord = (e) => {
        e.preventDefault();
        const selectedVehicle = vehicles.find(v => v._id === form.vehicleId);
        const newRecord = {
            _id: Date.now().toString(),
            vehicle: selectedVehicle ? `${selectedVehicle.plateNumber} (${selectedVehicle.make} ${selectedVehicle.model})` : 'Unknown Vehicle',
            type: form.type,
            status: form.status,
            dueDate: form.dueDate,
            lastChecked: 'Just Now',
            cost: parseFloat(form.cost) || 0
        };
        setRecords([newRecord, ...records]);
        setShowModal(false);
        setForm({ vehicleId: '', type: '', status: 'Pending Renewal', dueDate: '', cost: '' });
    };

    const filtered = records.filter(r =>
        r.vehicle.toLowerCase().includes(search.toLowerCase()) ||
        r.type.toLowerCase().includes(search.toLowerCase())
    );

    const getStatusStyle = (status) => {
        if (status === 'Valid') return { bg: '#ecfdf5', color: '#059669', border: '#d1fae5' };
        if (status === 'Expired') return { bg: '#fef2f2', color: '#dc2626', border: '#fee2e2' };
        return { bg: '#fffbeb', color: '#d97706', border: '#fef3c7' };
    };

    return (
        <div className="cp-page">
            <style>{`
                .cp-page { padding: 30px; background: #f8fafc; min-height: 100vh; font-family: 'Inter', sans-serif; }
                .cp-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 24px; }
                .cp-title { font-size: 26px; font-weight: 800; color: #1e293b; margin: 0 0 4px 0; }
                .cp-subtitle { font-size: 14px; color: #64748b; margin: 0; }
                
                .cp-add-btn {
                    background: #2563eb; color: white; border: none; padding: 10px 22px;
                    border-radius: 8px; font-weight: 700; cursor: pointer; display: flex; align-items: center; gap: 8px;
                    font-size: 14px; box-shadow: 0 4px 12px rgba(37,99,235,0.2);
                }

                .cp-toolbar { margin-bottom: 16px; display: flex; justify-content: space-between; align-items: center; }
                .cp-search-wrap { position: relative; width: 350px; }
                .cp-search-wrap svg { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: #94a3b8; }
                .cp-search-input {
                    width: 100%; padding: 10px 14px 10px 38px; border: 1.5px solid #e2e8f0; border-radius: 10px;
                    font-size: 14px; outline: none; transition: all 0.2s;
                }
                .cp-search-input:focus { border-color: #2563eb; box-shadow: 0 0 0 3px rgba(37,99,235,0.05); }

                .cp-card { 
                    background: white; border-radius: 12px; border: 1px solid #e2e8f0; 
                    box-shadow: 0 4px 6px -1px rgba(0,0,0,0.03); overflow: hidden;
                }
                .cp-table { width: 100%; border-collapse: collapse; }
                .cp-table th { 
                    text-align: left; padding: 16px 20px; font-size: 12px; font-weight: 600; 
                    color: #94a3b8; border-bottom: 1px solid #f1f5f9; background: #f8fafc;
                }
                .cp-table td { padding: 16px 20px; font-size: 14px; color: #334155; border-bottom: 1px solid #f1f5f9; }
                .cp-table tr:hover { background: #fafbfc; }

                .cp-name-cell { font-weight: 700; color: #1e293b; display: flex; align-items: center; gap: 10px; }
                
                .cp-status-badge {
                    padding: 4px 12px; border-radius: 99px; font-size: 11px; font-weight: 700; text-transform: uppercase;
                    border: 1px solid transparent; display: inline-block;
                }

                .cp-actions { display: flex; gap: 8px; }
                .cp-act-btn { 
                    border: none; background: none; padding: 6px; cursor: pointer; border-radius: 6px;
                    color: #94a3b8; transition: all 0.2s; display: flex; align-items: center; justify-content: center;
                }
                .cp-act-btn:hover { background: #f1f5f9; color: #2563eb; }
                .cp-act-btn.del:hover { background: #fef2f2; color: #ef4444; }

                /* Modal */
                .cp-modal-overlay {
                    position: fixed; inset: 0; background: rgba(0,0,0,0.4); backdrop-filter: blur(4px);
                    display: flex; align-items: center; justify-content: center; z-index: 1000;
                }
                .cp-modal { background: white; width: 600px; border-radius: 20px; padding: 30px; position: relative; }
                .cp-modal-close { position: absolute; top: 20px; right: 20px; cursor: pointer; color: #94a3b8; }
                .cp-modal-title { font-size: 20px; font-weight: 800; color: #1e293b; margin-bottom: 24px; }
                
                .cp-form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-bottom: 20px; }
                .cp-form-group { display: flex; flex-direction: column; gap: 6px; }
                .cp-form-group.full { grid-column: span 2; }
                .cp-label { font-size: 12px; font-weight: 700; color: #475569; }
                .cp-input, .cp-select {
                    padding: 10px 14px; border: 1.5px solid #e2e8f0; border-radius: 10px; font-size: 14px;
                    outline: none; transition: all 0.2s; background: #fff;
                }
                .cp-input:focus, .cp-select:focus { border-color: #2563eb; box-shadow: 0 0 0 3px rgba(37,99,235,0.1); }
                
                .cp-save-btn {
                    width: 100%; background: #2563eb; color: white; border: none; padding: 12px;
                    border-radius: 10px; font-weight: 700; cursor: pointer; margin-top: 10px;
                }
                .cp-empty-state { padding: 60px; text-align: center; color: #94a3b8; }
            `}</style>

            <div className="cp-header">
                <div>
                    <h1 className="cp-title">Compliance & Renewals</h1>
                    <p className="cp-subtitle">Track insurance, permits, and regulatory compliance for your fleet.</p>
                </div>
                <button className="cp-add-btn" onClick={() => setShowModal(true)}>
                    <Icon d={ic.plus} size={18} strokeWidth={2.5} /> Add Record
                </button>
            </div>

            <div className="cp-toolbar">
                <div className="cp-search-wrap">
                    <Icon d={ic.search} size={16} />
                    <input
                        type="text"
                        placeholder="Search vehicle or type..."
                        className="cp-search-input"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>
            </div>

            <div className="cp-card">
                <table className="cp-table">
                    <thead>
                        <tr>
                            <th>Vehicle</th>
                            <th>Compliance Type</th>
                            <th>Status</th>
                            <th>Due Date</th>
                            <th>Last Checked</th>
                            <th>Cost</th>
                            <th style={{ width: 80, textAlign: 'center' }}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.length === 0 ? (
                            <tr>
                                <td colSpan="7">
                                    <div className="cp-empty-state">
                                        <Icon d={ic.alert} size={48} color="#e2e8f0" style={{ marginBottom: 16 }} />
                                        <div style={{ fontWeight: 600 }}>No compliance records found.</div>
                                    </div>
                                </td>
                            </tr>
                        ) : (
                            filtered.map(rec => {
                                const st = getStatusStyle(rec.status);
                                return (
                                    <tr key={rec._id}>
                                        <td><div className="cp-name-cell">{rec.vehicle}</div></td>
                                        <td><span style={{ fontWeight: 600, color: '#2563eb' }}>{rec.type}</span></td>
                                        <td>
                                            <span className="cp-status-badge" style={{ background: st.bg, color: st.color, borderColor: st.border }}>
                                                {rec.status}
                                            </span>
                                        </td>
                                        <td>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#64748b' }}>
                                                <Icon d={ic.calendar} size={13} />
                                                {rec.dueDate}
                                            </div>
                                        </td>
                                        <td style={{ color: '#94a3b8', fontSize: 13 }}>{rec.lastChecked}</td>
                                        <td style={{ fontWeight: 700 }}>{formatCurrency(rec.cost, operatorDetails?.country)}</td>
                                        <td>
                                            <div className="cp-actions">
                                                <button className="cp-act-btn" title="Edit"><Icon d={ic.edit} size={16} /></button>
                                                <button className="cp-act-btn del" title="Delete" onClick={() => setRecords(records.filter(r => r._id !== rec._id))}><Icon d={ic.trash} size={16} /></button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>

            {showModal && (
                <div className="cp-modal-overlay">
                    <div className="cp-modal">
                        <div className="cp-modal-close" onClick={() => setShowModal(false)}>
                            <Icon d={ic.x} size={20} />
                        </div>
                        <h2 className="cp-modal-title">Add Compliance Record</h2>
                        <form onSubmit={handleAddRecord}>
                            <div className="cp-form-grid">
                                <div className="cp-form-group full">
                                    <label className="cp-label">Vehicle *</label>
                                    <select
                                        className="cp-select"
                                        required
                                        value={form.vehicleId}
                                        onChange={(e) => setForm({ ...form, vehicleId: e.target.value })}
                                    >
                                        <option value="">Select vehicle</option>
                                        {vehicles.map(v => (
                                            <option key={v._id} value={v._id}>{v.plateNumber} ({v.make} {v.model})</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="cp-form-group">
                                    <label className="cp-label">Compliance Type *</label>
                                    <select
                                        className="cp-select"
                                        required
                                        value={form.type}
                                        onChange={(e) => setForm({ ...form, type: e.target.value })}
                                    >
                                        <option value="">Select type</option>
                                        {complianceTypes.map(t => (
                                            <option key={t._id} value={t.name}>{t.name}</option>
                                        ))}
                                        <option value="Insurance">Insurance</option>
                                        <option value="Road Permit">Road Permit</option>
                                        <option value="Inspection">Inspection</option>
                                    </select>
                                </div>
                                <div className="cp-form-group">
                                    <label className="cp-label">Status *</label>
                                    <select
                                        className="cp-select"
                                        required
                                        value={form.status}
                                        onChange={(e) => setForm({ ...form, status: e.target.value })}
                                    >
                                        <option value="Valid">Valid</option>
                                        <option value="Expired">Expired</option>
                                        <option value="Pending Renewal">Pending Renewal</option>
                                    </select>
                                </div>
                                <div className="cp-form-group">
                                    <label className="cp-label">Due Date *</label>
                                    <input
                                        type="date"
                                        className="cp-input"
                                        required
                                        value={form.dueDate}
                                        onChange={(e) => setForm({ ...form, dueDate: e.target.value })}
                                    />
                                </div>
                                <div className="cp-form-group">
                                    <label className="cp-label">Cost ({operatorDetails?.defaultCurrency || 'USD'})</label>
                                    <input
                                        type="number"
                                        step="0.001"
                                        className="cp-input"
                                        placeholder="0.000"
                                        value={form.cost}
                                        onChange={(e) => setForm({ ...form, cost: e.target.value })}
                                    />
                                </div>
                            </div>
                            <button type="submit" className="cp-save-btn">Add Record</button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
